import { useTheme, Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import { formatInTimeZone } from 'date-fns-tz';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexCenter from 'components/FlexCenter';


const AssessmentCard = ({ row, index, canEdit, canDelete, onEdit, onDelete }) => {
    const theme = useTheme();
    const dateStr = row.date ? formatInTimeZone(new Date(row.date), 'UTC', 'dd/MM/yyyy') : '—';
    const effectiveToStr = row.effectiveTo ? new Date(row.effectiveTo).toLocaleDateString() : null;

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                borderLeft: `2px solid ${theme.palette.primary.main}`, 
                borderRadius: 1 
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    #{index + 1}
                </Typography>
                <Typography variant="subtitle2" noWrap flex={1} minWidth={0}>
                    {row.crewCode} {row.crewName}
                </Typography>
                {canEdit && (
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(row._id)}
                            sx={{ color: theme.palette.primary.dark, '&:hover': { color: theme.palette.green?.deep } }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canDelete && (
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => onDelete(row._id)}
                            sx={{ color: theme.palette.primary.dark, '&:hover': { color: theme.palette.green?.deep } }}>
                            <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Box display="flex" gap={1} mt={0.75} flexWrap="wrap" alignItems="center">
                <Typography variant="caption" color="text.secondary">{dateStr}</Typography>
                {row.performanceType && (
                    <Typography variant="caption" fontWeight="medium">· {row.performanceType}</Typography>
                )}
                {row.level && (
                    <Typography variant="caption" fontWeight="medium">· {row.level}</Typography>
                )}
                {row.kpi && (
                    <Typography variant="caption" color="primary" fontWeight="bold">· KPI: {row.kpi}</Typography>
                )}
            </Box>

            {row.description && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    {row.description}
                </Typography>
            )}

            {(row.disciplinaryAction || effectiveToStr) && (
                <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                    {row.disciplinaryAction && (
                        <Typography variant="caption" color="text.secondary">
                            Disciplinary: {row.disciplinaryAction}
                        </Typography>
                    )}
                    {effectiveToStr && (
                        <Typography variant="caption" color="text.secondary">
                            · EffectiveTo: {effectiveToStr}
                        </Typography>
                    )}
                </Box>
            )}
        </Paper>
    );
};


const CrewAssessmentList = ({ rows, canEdit, canDelete, onEdit, onDelete }) => {
    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            width="100%"
            gap={2}
        >
            {rows.map((row, index) => (
                <AssessmentCard
                    key={row._id}
                    row={row}
                    index={index}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onEdit={onEdit}
                    onDelete={onDelete}
                />
            ))}
        </Box>
    );
};

export default CrewAssessmentList;
